import React from 'react'
import { Link } from 'react-router-dom'
import { HanddrawnArrow } from './HanddrawnArrow'
import { ArcOrbitButton } from './ArcOrbitButton'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-[#36454F] text-white pt-20 pb-12 relative overflow-hidden border-t-4 border-[#007A53]">
      {/* Organisk baggrundsdekoration */}
      <div
        className="absolute -top-24 -right-24 w-96 h-96 rounded-organic bg-[#007A53]/10 pointer-events-none blur-2xl"
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-24 -left-24 w-96 h-96 rounded-organic bg-[#2F6F73]/15 pointer-events-none blur-2xl"
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-14 border-b border-white/15">
          {/* Brand & Mission column (lg:col-span-5) */}
          <div className="lg:col-span-5 space-y-6">
            <Link
              to="/"
              className="inline-block focus:outline-none rounded-2xl transition-transform duration-300 hover:scale-[1.02]"
              aria-label="Emmerske Efterskole"
            >
              <div className="bg-white px-3.5 py-2 rounded-xl inline-block shadow-sm">
                <img
                  src="https://assets.auxodev.dk/assets/e40f0695-6390-4586-b67e-d6f4d574a533/ee-nylogo-trans.png"
                  alt="Emmerske Efterskole"
                  className="h-10 sm:h-12 w-auto object-contain"
                />
              </div>
            </Link>

            <p className="text-white/80 text-sm sm:text-base leading-relaxed max-w-md font-medium">
              Emmerske Efterskole er en specialiseret efterskole for ordblinde i Sønderjylland med
              plads til 95 elever i 8., 9. og 10. klasse. Her er det rart at være og lære.
            </p>

            <div className="pt-1">
              <ArcOrbitButton
                to="/rundvisning"
                variant="white"
                icon={<i className="fa-solid fa-calendar-check text-[#007A53] text-base" aria-hidden="true" />}
                className="w-full sm:w-auto"
              >
                Book en personlig rundvisning
              </ArcOrbitButton>
            </div>

            {/* Sociale links med hover scale (Afsnit 15) */}
            <div className="pt-2 flex items-center gap-3">
              <a
                href="https://www.facebook.com/emmerskeefterskole"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#007A53] text-white flex items-center justify-center transition-all duration-200 hover:scale-110"
                aria-label="Følg Emmerske Efterskole på Facebook"
              >
                <i className="fa-brands fa-facebook-f text-sm" />
              </a>
              <a
                href="https://www.instagram.com/emmerske_efterskole/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#007A53] text-white flex items-center justify-center transition-all duration-200 hover:scale-110"
                aria-label="Følg Emmerske Efterskole på Instagram"
              >
                <i className="fa-brands fa-instagram text-sm" />
              </a>
              <a
                href="https://www.youtube.com/@emmerskeefterskole"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#007A53] text-white flex items-center justify-center transition-all duration-200 hover:scale-110"
                aria-label="Se videoer på YouTube"
              >
                <i className="fa-brands fa-youtube text-sm" />
              </a>
            </div>
          </div>

          {/* Kolonne 1: Kontakt & Skoleinfo (lg:col-span-3) */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="font-['Plus_Jakarta_Sans'] text-white font-extrabold text-base sm:text-lg tracking-tight">
              Kontakt
            </h3>
            <div className="text-sm space-y-2.5 text-white/80 font-medium">
              <p className="text-white font-semibold">Åbenråvej 14, 6270 Tønder</p>
              <p>
                <span className="text-white/60">Tlf: </span>
                <a
                  href="tel:74724433"
                  className="hover:underline hover:text-white transition-colors font-bold text-white"
                >
                  74 72 44 33
                </a>
              </p>
              <p>
                <span className="text-white/60">E-mail: </span>
                <a
                  href="mailto:ee@eeskole.dk"
                  className="hover:underline hover:text-white transition-colors text-white"
                >
                  ee@eeskole.dk
                </a>
              </p>
              <p>
                <span className="text-white/60">CVR: </span>
                83992611
              </p>
              <p>
                <span className="text-white/60">EAN: </span>
                579 000 249 8367
              </p>

              <div className="pt-2">
                <a
                  href="https://maps.google.com/?q=%C3%85benr%C3%A5vej+14,+6270+T%C3%B8nder"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#DCEEF2] hover:text-white hover:underline underline-offset-4"
                >
                  <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
                  <span>Få rutevejledning på Google Maps</span>
                </a>
              </div>
            </div>
          </div>

          {/* Kolonne 2: Quicklinks (lg:col-span-2) */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="font-['Plus_Jakarta_Sans'] text-white font-extrabold text-base sm:text-lg tracking-tight">
              Quicklinks
            </h3>
            <ul className="space-y-2 text-sm font-medium">
              <li>
                <Link to="/om-os/oekonomi" className="text-white/80 hover:text-white transition-colors">
                  Økonomi
                </Link>
              </li>
              <li>
                <Link to="/valgfag" className="text-white/80 hover:text-white transition-colors">
                  Valgfag
                </Link>
              </li>
              <li>
                <Link
                  to="/undervisning/tekniske-hjaelpemidler"
                  className="text-white/80 hover:text-white transition-colors"
                >
                  Tekniske hjælpemidler
                </Link>
              </li>
              <li>
                <Link to="/lovkrav" className="text-white/80 hover:text-white transition-colors">
                  Persondatapolitik
                </Link>
              </li>
              <li>
                <a
                  href="https://toender.sport24klubshops.dk/shop/emmerske-efterskole-27c1.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors inline-flex items-center gap-1.5"
                >
                  <span>Emmerske webshop</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-70" />
                </a>
              </li>
              <li>
                <a
                  href="https://eeskole.viggo.dk/Basic/Account/Login?returnUrl=%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors inline-flex items-center gap-1.5"
                >
                  <span>Gå til VIGGO</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-70" />
                </a>
              </li>
            </ul>
          </div>

          {/* Kolonne 3: Om skolen (lg:col-span-2) */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="font-['Plus_Jakarta_Sans'] text-white font-extrabold text-base sm:text-lg tracking-tight">
              Om skolen
            </h3>
            <ul className="space-y-2 text-sm font-medium">
              <li>
                <Link to="/om-os" className="text-white/80 hover:text-white transition-colors">
                  Om Emmerske Efterskole
                </Link>
              </li>
              <li>
                <Link to="/dld" className="text-white/80 hover:text-white transition-colors">
                  Når dit barn har DLD
                </Link>
              </li>
              <li>
                <Link to="/apd" className="text-white/80 hover:text-white transition-colors">
                  Når dit barn har APD
                </Link>
              </li>
              <li>
                <Link to="/hverdagen" className="text-white/80 hover:text-white transition-colors">
                  Hverdagen på skolen
                </Link>
              </li>
              <li>
                <Link to="/faciliteter" className="text-white/80 hover:text-white transition-colors">
                  Faciliteter & værksteder
                </Link>
              </li>
              <li>
                <Link to="/lovkrav" className="text-white/80 hover:text-white transition-colors">
                  Lovkrav & vedtægter
                </Link>
              </li>
              <li>
                <Link to="/billeder" className="text-white/80 hover:text-white transition-colors">
                  Billeder
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/70 font-medium">
          <p>© {currentYear} Emmerske Efterskole. Alle rettigheder forbeholdes.</p>
          <div className="flex flex-wrap items-center gap-6">
            <Link to="/rundvisning" className="hover:text-white transition-colors">
              Book rundvisning
            </Link>
            <Link to="/kontakt" className="hover:text-white transition-colors">
              Kontakt os
            </Link>
            <a
              href="https://maps.google.com/?q=%C3%85benr%C3%A5vej+14,+6270+T%C3%B8nder"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              Find vej
            </a>
            <Link
              to="/admin/rundvisning"
              className="hover:text-white transition-colors opacity-70 hover:opacity-100 flex items-center gap-1"
            >
              <i className="fa-solid fa-lock text-[10px]" />
              <span>Admin</span>
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
